import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import styled, { css } from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import useLoading from "components/atoms/loading/useLoading";
import useToast from "components/atoms/toast/useToast";

import ContextMenu from "components/molecules/ContextMenu";
import SpacePaymentNewModal from "components/page/modal/SpacePaymentNewModal";

import { useSpace, useSpaceMemberUpdate, useUser } from "hooks/queries/useUser";

import Constants from "utils/constants";

const SPACE_ICON_SIZE = 30;

const SpaceBarConatiner = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${Constants.SPACE_BAR_WIDTH}px;
    height: calc(100vh - ${Constants.MOBILE_HEADER_HEIGHT}px);
    background-color: var(--color-White);
    border-right: 1px solid var(--color-Grey3);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 0;
    z-index: 7;
`;

const SpaceSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const SpaceSectionTop = styled(SpaceSection)`
    height: 46px;
    justify-content: center;
    flex-shrink: 0;
`;

const SpaceSectionBottom = styled(SpaceSection)`
    margin-bottom: 16px;
`;

const SpaceSectionMiddle = styled(SpaceSection)`
    flex: 1;
`;

const SpaceDivider = styled.div`
    width: 28px;
    height: 1px;
    background: var(--color-Grey3);
    margin: 6px 0 6px 0;
`;

const SpaceItem = styled.div`
    margin: 4px 0;
`;

const SpaceIconWrap = styled.div`
    position: relative;
    display: inline-flex;
`;

const SpaceCircleButton = styled.button`
    width: ${SPACE_ICON_SIZE}px;
    height: ${SPACE_ICON_SIZE}px;
    border-radius: 50%;
    border: 0;
    outline: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: ${(p) => (p.readonly ? "not-allowed" : "pointer")};
    box-shadow: ${(p) =>
        p.$active
            ? "0 0 0 2px var(--color-White), 0 0 0 4px var(--color-Key)"
            : p.$image
            ? "0 0 0 1px var(--color-Line)"
            : "none"};
    background-color: ${(p) => (p.$active ? p.bg || "var(--color-Primary)" : "var(--color-White)")};
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
    &:hover {
        background-color: ${(p) => (p.readonly ? "transparent" : "var(--color-ButtonHover4)")};
    }
    &:focus-visible {
        box-shadow: 0 0 0 2px var(--color-Key), 0 0 0 4px rgba(0, 0, 0, 0.06);
    }
`;

const SpaceFunctionButton = styled.button`
    width: ${SPACE_ICON_SIZE}px;
    height: ${SPACE_ICON_SIZE}px;
    border: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: ${(p) => (p.readonly ? "not-allowed" : "pointer")};
    background-color: ${(p) => (p.$active ? p.bg || "var(--color-Primary)" : "var(--color-White)")};
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
    &:hover {
        background-color: ${(p) => (p.readonly ? "transparent" : "var(--color-ButtonHover4)")};
    }
    &:focus-visible {
        box-shadow: 0 0 0 2px var(--color-Key), 0 0 0 4px rgba(0, 0, 0, 0.06);
    }
`;

const SpaceAvatarImg = styled.img`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    display: block;

    filter: ${(p) => {
        if (p.$status === Constants.SPACE_STATUS_INACTIVE) return "grayscale(70%) contrast(0.9)";
        return "none";
    }};
    pointer-events: none;
    user-select: none;
`;

const SpaceAvatarFallback = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.4rem;
    font-weight: 600;
    text-transform: uppercase;

    background-color: var(--color-ButtonHover4);
    color: var(--color-Black);

    filter: ${(p) => (p.$status === Constants.SPACE_STATUS_INACTIVE ? "grayscale(70%) contrast(0.9)" : "none")};
    user-select: none;
    pointer-events: none;
`;

const HiddenItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    cursor: default;
`;

const HiddenItemName = styled.span`
    flex: 1;
    font-size: 1.3rem;
    color: var(--color-Black);
`;

const HiddenItemUnhide = styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    margin: 0;
    font-size: 1.3rem;
    color: var(--color-Key);
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        opacity: 0.85;
    }
`;

const HiddenItemAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: ${(p) => (p.$image ? "0 0 0 1px var(--color-SubBlack)" : "none")};
    flex-shrink: 0;
`;

const HiddenItemImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;

    filter: ${(p) => (p.$status === "INACTIVE" ? "grayscale(70%) contrast(0.9)" : "none")};
`;

const HiddenItemFallback = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 1.3rem;
    font-weight: 600;
    text-transform: uppercase;

    background-color: var(--color-ButtonHover4);
    color: var(--color-Black);

    filter: ${(p) => (p.$status === "INACTIVE" ? "grayscale(70%) contrast(0.9)" : "none")};
`;

const SpaceBadge = styled.span`
    position: absolute;
    top: -2px;
    right: -2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 999px;

    /* variant에 따라 사이즈/스타일 */
    ${(p) =>
        p.variant === "count"
            ? css`
                  min-width: 18px;
                  height: 18px;
                  padding: 0 6px;
                  font-size: 11px;
                  line-height: 1;
                  font-weight: 700;
              `
            : p.variant === "icon"
            ? css`
                  width: 12px;
                  height: 12px;
              `
            : css`
                  /* dot */
                  width: 8px;
                  height: 8px;
              `}

    background: ${(p) => p.bg || "var(--color-Key)"};
    color: ${(p) => p.color || "#fff"};
`;

function SpaceBar() {
    const navigate = useNavigate();
    const { space_uuid } = useParams();

    const { setToast } = useToast();
    const { setLoading } = useLoading();
    const user = useUser().data;

    const changeSpace = (targetPathname) => {
        navigate(targetPathname);
    };

    const [showContextMenu, setShowContextMenu] = useState({
        space_uuid: null,
        space_member_id: null,
        x: 0,
        y: 0,
    });

    const [showHiddenSpace, setShowHiddenSpace] = useState({
        space_uuid: null,
        space_member_id: null,
        show: false,
        x: 0,
        y: 0,
    });

    const spaceQuery = useSpace();
    const spaceMemberUpdateMutation = useSpaceMemberUpdate(); // 숨김 처리 업데이트 용도
    const spaces = React.useMemo(() => {
        if (!spaceQuery.data) return [];
        return [...spaceQuery.data].sort((a, b) => {
            const aTime = a?.my_member_info?.started_at ? new Date(a.my_member_info.started_at).getTime() : 0;
            const bTime = b?.my_member_info?.started_at ? new Date(b.my_member_info.started_at).getTime() : 0;
            return aTime - bTime;
        });
    }, [spaceQuery.data]);

    const [showSubscribeModal, setShowSubscribeModal] = useState();

    const visibleSpaces = spaces.filter((space) => !space.my_member_info.is_hidden);
    const privateSpace = visibleSpaces.find((space) => space.scope === Constants.SPACE_SCOPE_PRIVATE);
    const groupSpaces = visibleSpaces.filter((space) => space.scope === Constants.SPACE_SCOPE_GROUP);
    return (
        <div>
            <SpaceBarConatiner>
                <SpaceSectionTop>
                    {privateSpace && (
                        <SpaceItem className={"space-item-" + privateSpace.id} key={privateSpace.id}>
                            <Tooltip message={privateSpace.name} key={privateSpace.uuid}>
                                <SpaceIconWrap>
                                    <SpaceCircleButton
                                        $active={space_uuid === privateSpace.uuid}
                                        onClick={() => {
                                            changeSpace(`/${privateSpace.uuid}/reference`);
                                        }}
                                        $image={!!privateSpace.image}
                                    >
                                        {privateSpace.image ? (
                                            <SpaceAvatarImg
                                                src={privateSpace.image}
                                                alt={privateSpace.name}
                                                $status={privateSpace.status}
                                            />
                                        ) : (
                                            <SpaceAvatarFallback $status={privateSpace.status}>
                                                {privateSpace.name?.trim()?.[0] || "?"}
                                            </SpaceAvatarFallback>
                                        )}
                                    </SpaceCircleButton>
                                </SpaceIconWrap>
                            </Tooltip>
                        </SpaceItem>
                    )}
                </SpaceSectionTop>

                <SpaceDivider />

                <SpaceSectionMiddle>
                    {/* 나머지 스페이스들 */}
                    {groupSpaces.map((space) => (
                        <SpaceItem className={"space-item-" + space.id} key={space.id}>
                            <Tooltip message={`${space.name}`} key={space.uuid}>
                                <SpaceIconWrap>
                                    <SpaceCircleButton
                                        $active={space_uuid === space.uuid}
                                        onClick={() => {
                                            if (
                                                space.my_member_info.invitation_status ===
                                                Constants.SPACE_MEMBER_INVITATION_STATUS_PENDING
                                            ) {
                                                changeSpace(`/${space.uuid}/management`);
                                            } else {
                                                changeSpace(`/${space.uuid}/reference`);
                                            }
                                        }}
                                        onContextMenu={(e) => {
                                            setShowContextMenu(
                                                showContextMenu.uuid === space.uuid
                                                    ? { space_uuid: null, space_member_id: null, x: 0, y: 0 }
                                                    : {
                                                          space_uuid: space.uuid,
                                                          space_member_id: space.my_member_info.id,
                                                          x: e.currentTarget.getBoundingClientRect().left,
                                                          y: e.currentTarget.getBoundingClientRect().bottom,
                                                      },
                                            );
                                            e.preventDefault();
                                        }}
                                        $image={!!space.image}
                                    >
                                        {space.image ? (
                                            <SpaceAvatarImg src={space.image} alt={space.name} $status={space.status} />
                                        ) : (
                                            <SpaceAvatarFallback $status={space.status}>
                                                {space.name?.trim()?.[0] || "?"}
                                            </SpaceAvatarFallback>
                                        )}
                                    </SpaceCircleButton>
                                    {space.my_member_info.invitation_status ===
                                        Constants.SPACE_MEMBER_INVITATION_STATUS_PENDING && (
                                        <SpaceBadge variant="icon" bg={"var(--color-Key)"}>
                                            {/* <Icon name={"info"} size={"12"} /> */}
                                        </SpaceBadge>
                                    )}
                                </SpaceIconWrap>
                            </Tooltip>
                        </SpaceItem>
                    ))}

                    {showContextMenu.space_uuid !== null && (
                        <ContextMenu
                            show={true}
                            position={{ x: showContextMenu.x, y: showContextMenu.y - 20 }}
                            minWidth={"70px"}
                            items={[
                                {
                                    name: "숨기기",
                                    action: async (e) => {
                                        try {
                                            setLoading(true);
                                            await spaceMemberUpdateMutation.mutateAsync({
                                                space_uuid: showContextMenu.space_uuid,
                                                space_member_id: showContextMenu.space_member_id,
                                                formData: {
                                                    is_hidden: true,
                                                },
                                            });
                                            setShowContextMenu({
                                                space_uuid: null,
                                                space_member_id: null,
                                                x: 0,
                                                y: 0,
                                            });
                                            setToast("선택한 스페이스를 숨겼습니다.", "info");
                                            changeSpace(`/${privateSpace.uuid}/reference`);
                                        } catch (error) {
                                            Alert("warn", "에러", "스페이스 숨기기에 실패했습니다.");
                                        } finally {
                                            setLoading(false);
                                        }
                                    },
                                },
                            ]}
                            onClose={() => {
                                setShowContextMenu({
                                    space_uuid: null,
                                    space_member_id: null,
                                    x: 0,
                                    y: 0,
                                });
                            }}
                        />
                    )}

                    <SpaceItem>
                        <Tooltip message={"그룹 스페이스 추가"}>
                            <SpaceFunctionButton
                                onClick={() => {
                                    setShowSubscribeModal(true);
                                }}
                                $image={true}
                            >
                                <Icon name={"plus"} size={"18"} />
                            </SpaceFunctionButton>
                        </Tooltip>
                    </SpaceItem>
                </SpaceSectionMiddle>

                <SpaceDivider />

                <SpaceSectionBottom>
                    <SpaceItem>
                        <Tooltip
                            message={`숨긴 스페이스 (${
                                spaces.filter((space) => space.my_member_info.is_hidden).length || 0
                            })`}
                        >
                            <SpaceFunctionButton
                                onClick={(e) => {
                                    setShowHiddenSpace((prevState) => ({
                                        show: !prevState.show,
                                        x: e.currentTarget?.getBoundingClientRect().left,
                                        y: e.currentTarget?.getBoundingClientRect().bottom - 20,
                                    }));
                                    e.preventDefault();
                                }}
                                disabled={spaces.filter((space) => space.my_member_info.is_hidden).length === 0}
                            >
                                <Icon name={"storage"} size={"20"} />
                            </SpaceFunctionButton>

                            {showHiddenSpace.show && (
                                <ContextMenu
                                    title="숨긴 스페이스"
                                    show={true}
                                    position={{ x: showHiddenSpace.x, y: showHiddenSpace.y }}
                                    minWidth={"180px"}
                                    items={spaces
                                        .filter((space) => space.my_member_info.is_hidden)
                                        .map((space) => ({
                                            name: (
                                                <HiddenItemRow>
                                                    <HiddenItemAvatar $image={space.image}>
                                                        {space.image ? (
                                                            <HiddenItemImg
                                                                src={space.image}
                                                                alt={space.name}
                                                                $status={space.status}
                                                            />
                                                        ) : (
                                                            <HiddenItemFallback $status={space.status}>
                                                                {space.name?.trim()?.[0] || "?"}
                                                            </HiddenItemFallback>
                                                        )}
                                                    </HiddenItemAvatar>

                                                    <HiddenItemName>{space.name}</HiddenItemName>
                                                    <HiddenItemUnhide
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                setLoading(true);
                                                                await spaceMemberUpdateMutation.mutateAsync({
                                                                    space_uuid: space.uuid,
                                                                    space_member_id: space.my_member_info.id,
                                                                    formData: {
                                                                        is_hidden: false,
                                                                    },
                                                                });
                                                                setShowHiddenSpace({
                                                                    show: false,
                                                                    x: 0,
                                                                    y: 0,
                                                                });
                                                                setToast("스페이스 숨기기가 해제되었습니다.", "info");
                                                                changeSpace(`/${space.uuid}/reference`);
                                                            } catch (error) {
                                                                Alert(
                                                                    "warn",
                                                                    "에러",
                                                                    "스페이스 숨기기 해제에 실패했습니다.",
                                                                );
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        }}
                                                    >
                                                        해제
                                                    </HiddenItemUnhide>
                                                </HiddenItemRow>
                                            ),
                                        }))}
                                    onClose={() => {
                                        setShowHiddenSpace({
                                            show: false,
                                            x: 0,
                                            y: 0,
                                        });
                                    }}
                                    // triggerClassName={`tree-knob-${g.id}`}
                                />
                            )}
                        </Tooltip>
                    </SpaceItem>
                </SpaceSectionBottom>
            </SpaceBarConatiner>
            {showSubscribeModal && (
                <SpacePaymentNewModal isGroupPlan={true} user={user} exitModal={(e) => setShowSubscribeModal(false)} />
            )}
        </div>
    );
}

export default SpaceBar;
